import { useEffect, useState } from 'react';
import { Badge, Button, Card, ConfirmDialog } from '@timely/ui';
import { FormNotice } from '../screens/AuthLayout';
import { apiMessage } from '../lib/api';
import {
    type GooglePreview, type GoogleSheetKind, type GoogleStatus, type GoogleTab,
    disconnectGoogle, pickerConfig, previewGoogleSheet, startGoogleConnection,
} from './google';
import { loadPicker, openPicker } from './googlePicker';

/**
 * The company's Google account: connect it, reconnect it, disconnect it.
 *
 * One connection per company, used by the receivables sheet and the stock
 * sheet alike, so disconnecting here stops both — and the dialog says so.
 */
export function GoogleAccountCard({
    workspaceId, slug, kind, status, canManage, onChanged,
}: {
    workspaceId: string;
    slug: string;
    kind: GoogleSheetKind;
    status: GoogleStatus;
    canManage: boolean;
    onChanged: () => void;
}) {
    const [busy, setBusy] = useState<'connect' | 'disconnect' | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [confirming, setConfirming] = useState(false);

    const connect = async () => {
        if (busy) return;
        setBusy('connect');
        setError(null);
        try {
            await startGoogleConnection(workspaceId, slug, kind);
            // The browser is on its way to Google; nothing more happens here.
        } catch (failed) {
            setError(failed instanceof Error ? failed.message : apiMessage(failed));
            setBusy(null);
        }
    };

    const disconnect = async () => {
        setConfirming(false);
        setBusy('disconnect');
        setError(null);
        try {
            await disconnectGoogle(workspaceId);
            onChanged();
        } catch (failed) {
            setError(failed instanceof Error ? failed.message : apiMessage(failed));
        } finally {
            setBusy(null);
        }
    };

    if (!status.connected) {
        return (
            <Card className="mt-5 px-5 py-6">
                <h3 className="text-subhead font-semibold text-label tracking-heading">Connect your Google account</h3>
                <p className="text-body text-label-2 mt-2 leading-relaxed max-w-prose">
                    Timely Payment asks Google for access only to the spreadsheets you choose with it —
                    not your Drive, not your other files — and only ever reads them. Your sheet stays
                    private: nobody needs a link to it.
                </p>
                {error && <div className="mt-4"><FormNotice tone="error">{error}</FormNotice></div>}
                {canManage ? (
                    <div className="mt-5">
                        <Button variant="primary" disabled={busy !== null} onClick={() => void connect()} busy={busy === 'connect'}>
                            {busy === 'connect' ? 'Going to Google…' : 'Connect Google'}
                        </Button>
                    </div>
                ) : (
                    <p className="text-secondary text-label-3 mt-4">
                        Somebody who manages this company’s receivables data can connect it.
                    </p>
                )}
            </Card>
        );
    }

    return (
        <Card className="mt-5 px-5 py-5">
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-subhead font-semibold text-label">Google</span>
                        <Badge tone={status.needsReconnect ? 'dang' : 'pos'}>
                            {status.needsReconnect ? 'Needs connecting again' : 'Connected'}
                        </Badge>
                    </div>
                    <div className="text-secondary text-label-3 mt-1 break-all">
                        {status.account || 'A Google account'}
                        {status.connectedByName && ` · connected by ${status.connectedByMe ? 'you' : status.connectedByName}`}
                    </div>
                </div>
                {canManage && (
                    <div className="flex items-center gap-2 flex-none">
                        {status.needsReconnect && (
                            <Button size="sm" variant="primary" disabled={busy !== null} onClick={() => void connect()} busy={busy === 'connect'}>
                                {busy === 'connect' ? 'Going to Google…' : 'Connect again'}
                            </Button>
                        )}
                        <Button size="sm" variant="quiet" disabled={busy !== null} onClick={() => setConfirming(true)} busy={busy === 'disconnect'}>
                            {busy === 'disconnect' ? 'Disconnecting…' : 'Disconnect Google'}
                        </Button>
                    </div>
                )}
            </div>
            {status.needsReconnect && (
                <div className="mt-4">
                    <FormNotice tone="error">
                        Google no longer accepts this connection — access may have been removed in the Google
                        account’s settings. Connect Google again; the sheets you chose stay chosen.
                    </FormNotice>
                </div>
            )}
            {error && <div className="mt-4"><FormNotice tone="error">{error}</FormNotice></div>}

            <ConfirmDialog
                open={confirming}
                title="Disconnect Google?"
                confirmLabel="Disconnect"
                busy={busy === 'disconnect'}
                onCancel={() => setConfirming(false)}
                onConfirm={() => void disconnect()}
            >
                <p>
                    Timely Payment stops reading your receivables and stock sheets from Google until it is
                    connected again. Your customers, follow-ups, notes and cheques stay exactly as they are.
                </p>
            </ConfirmDialog>
        </Card>
    );
}

export interface ChosenGoogleSheet {
    spreadsheetId: string;
    spreadsheetTitle: string;
    tab: NonNullable<GooglePreview['tab']>;
}

/**
 * Choosing a spreadsheet with Google's Picker, then the exact tab, then
 * reading that tab's first rows for the mapping screen.
 *
 * Only for the person whose Google account is connected (the server refuses
 * anybody else, because the Picker shows that account's whole Drive); for
 * everybody else it says whose account it is and how to use their own.
 */
export function GoogleSheetChooser({
    workspaceId, status, start, onChosen, onCancel,
}: {
    workspaceId: string;
    status: GoogleStatus;
    /** A spreadsheet already chosen: skip the Picker and start at its tabs. */
    start?: { spreadsheetId: string };
    onChosen: (chosen: ChosenGoogleSheet) => void;
    onCancel?: () => void;
}) {
    const [step, setStep] = useState<'idle' | 'picking' | 'tabs' | 'reading'>(start ? 'tabs' : 'idle');
    const [spreadsheet, setSpreadsheet] = useState<{ id: string; title: string; tabs: GoogleTab[] } | null>(null);
    const [tabId, setTabId] = useState<number | null>(null);
    const [error, setError] = useState<string | null>(null);

    const listTabs = async (spreadsheetId: string) => {
        setError(null);
        setStep('tabs');
        try {
            const preview = await previewGoogleSheet(workspaceId, spreadsheetId);
            setSpreadsheet({ id: spreadsheetId, title: preview.spreadsheetTitle, tabs: preview.tabs });
            setTabId(null);
        } catch (failed) {
            setError(failed instanceof Error ? failed.message : apiMessage(failed));
            setStep('idle');
        }
    };

    const startId = start?.spreadsheetId;
    useEffect(() => {
        if (startId) void listTabs(startId);
        // eslint-disable-next-line react-hooks/exhaustive-deps -- once per spreadsheet handed in
    }, [startId]);

    const pick = async () => {
        setError(null);
        setStep('picking');
        try {
            const [config, picker] = await Promise.all([pickerConfig(workspaceId), loadPicker()]);
            const outcome = await openPicker(picker, config, window.location.origin);
            if (outcome.kind === 'cancelled') { setStep('idle'); return; }
            if (outcome.kind === 'refused') { setError(outcome.reason); setStep('idle'); return; }
            await listTabs(outcome.spreadsheetId);
        } catch (failed) {
            setError(failed instanceof Error ? failed.message : apiMessage(failed));
            setStep('idle');
        }
    };

    const read = async () => {
        if (!spreadsheet || tabId === null) return;
        setError(null);
        setStep('reading');
        try {
            const preview = await previewGoogleSheet(workspaceId, spreadsheet.id, tabId);
            if (!preview.tab) throw new Error('Google did not return that tab. Choose it again.');
            onChosen({ spreadsheetId: spreadsheet.id, spreadsheetTitle: preview.spreadsheetTitle, tab: preview.tab });
        } catch (failed) {
            setError(failed instanceof Error ? failed.message : apiMessage(failed));
            setStep('tabs');
        }
    };

    if (!status.connectedByMe) {
        return (
            <Card className="mt-5 px-5 py-5">
                <h3 className="text-subhead font-semibold text-label">Choosing a sheet</h3>
                <p className="text-body text-label-2 mt-2 leading-relaxed max-w-prose">
                    Only {status.connectedByName || 'the person'} who connected this Google account can choose
                    its sheets, because Google’s file chooser shows their whole Drive. To choose one
                    yourself, disconnect Google and connect it with your own account.
                </p>
            </Card>
        );
    }

    return (
        <Card className="mt-5 px-5 py-5">
            <h3 className="text-subhead font-semibold text-label">
                {spreadsheet ? `Which tab of “${spreadsheet.title || 'this spreadsheet'}”?` : 'Choose your spreadsheet'}
            </h3>
            {error && <div className="mt-3"><FormNotice tone="error">{error}</FormNotice></div>}

            {!spreadsheet ? (
                <div className="mt-3">
                    <p className="text-body text-label-2 leading-relaxed max-w-prose">
                        Google’s own file chooser opens here. Choose the spreadsheet; Timely Payment can read
                        that one and nothing else in your Drive.
                    </p>
                    <div className="mt-4 flex gap-2">
                        <Button variant="primary" disabled={step !== 'idle'} onClick={() => void pick()} busy={step === 'picking'}>
                            {step === 'picking' ? 'Opening Google…' : step === 'tabs' ? 'Reading…' : 'Choose in Google'}
                        </Button>
                        {onCancel && <Button variant="quiet" onClick={onCancel}>Cancel</Button>}
                    </div>
                </div>
            ) : (
                <div className="mt-3">
                    <fieldset>
                        <legend className="text-secondary text-label-3 mb-2">
                            Tabs are remembered by their number, so renaming one later is fine.
                        </legend>
                        {spreadsheet.tabs.map(tab => (
                            <label key={tab.sheetId} className="flex items-center gap-2.5 min-h-9">
                                <input
                                    type="radio" name="google-tab" className="h-4 w-4"
                                    checked={tabId === tab.sheetId}
                                    onChange={() => setTabId(tab.sheetId)}
                                />
                                <span className="text-body text-label">{tab.title}</span>
                            </label>
                        ))}
                    </fieldset>
                    <div className="mt-4 flex gap-2 flex-wrap">
                        <Button variant="primary" disabled={tabId === null || step === 'reading'} onClick={() => void read()} busy={step === 'reading'}>
                            {step === 'reading' ? 'Reading…' : 'Read this tab'}
                        </Button>
                        <Button variant="secondary" disabled={step === 'reading'} onClick={() => { setSpreadsheet(null); void pick(); }}>
                            A different spreadsheet
                        </Button>
                        {onCancel && <Button variant="quiet" onClick={onCancel}>Cancel</Button>}
                    </div>
                </div>
            )}
        </Card>
    );
}
